import { CSVOutputData } from '@/domain/models/CSV';
import {
  ImportPos,
  PosDataLevel,
  PosItem,
  PosOption,
  RegisterPos,
} from '@/domain/models/Pos';
import { CSVDataDomainService, DateDomainService } from '@/domain/services';
import {
  mappingPosDataLevel,
  MappingPosDataLevelCsvData,
  PosItemAndHandlePos,
  PosPromotionOneFreeCSVHeaders,
  posPromotionOneFreeCSVHeaders,
} from '@/services/Pos';

type Header = typeof posPromotionOneFreeCSVHeaders[number]['label'];

type PartiallyOptional<T, K extends keyof T> = Partial<Pick<T, K>> &
  Pick<T, Exclude<keyof T, K>>;

// 指定したプロパティをオプショナルにする。
type CSVItem = PartiallyOptional<
  CSVOutputData<PosPromotionOneFreeCSVHeaders>,
  | 'brand_name'
  | 'buy_one_get_one_free_pos_item_code'
  | 'auth_store_name'
  | 'delivery_service_name'
>;

export class PosDataFactory {
  static createHandlePosItems(
    posItems: PosItem[],
    posItemsAtStorage?: PosItemAndHandlePos[]
  ): PosItemAndHandlePos[] {
    return posItems
      .map((posItem: PosItem) =>
        this.addHandlingState(posItem, posItemsAtStorage)
      )
      .sort(this.sortDescAtPosItemCode);
  }

  static createHandlePosItemsAtReFeatch(
    posItems: PosItem[]
  ): PosItemAndHandlePos[] {
    return posItems
      .map(this.addHandlingStateAtRefetch)
      .sort(this.sortDescAtPosItemCode);
  }

  private static initialPosItem(posItem: PosItem) {
    return {
      ...posItem,

      // NOTE undefinedの考慮を後にしたくないためundefinedであれば空文字をいれている。
      pos_item_code: posItem.pos_item_code ?? '',
      buy_one_get_one_free_pos_item_code:
        posItem.buy_one_get_one_free_pos_item_code ?? '',
      posCode: posItem.pos_item_code ?? '',
      promotionOneFreePosCode: posItem.buy_one_get_one_free_pos_item_code ?? '',
    };
  }

  /**
   * 1: posCodeとpromotionOneFreePosCodeはインプットの入力テキストをハンドリングするために使用
   * 2: ローカルストレージにあるデーターとDBから取得した最新のposItemsを比較して、
   *    ローカルストレージがある場合にposCodeとpromotionOneFreePosCodeは
   *    ローカルストレージにある最新のものを使用する。
   */
  private static addHandlingState(
    posItem: PosItem, // APIから取得をした最新のposItem
    posItemsAtStorage?: PosItemAndHandlePos[] // 調整をしてローカルストレージに保存をさせているposItems
  ) {
    // ローカルストレージにあるかをみにいく
    const posItemAtStorage = posItemsAtStorage?.find(
      (posItemAtStorage) =>
        JSON.stringify(posItemAtStorage.meta_data) ===
        JSON.stringify(posItem.meta_data)
    );

    if (posItemAtStorage === undefined) {
      return PosDataFactory.initialPosItem(posItem);
    } else {
      return {
        ...posItem,

        // 入力がされていればその値が入る。されていなければ空百道を入れている。
        pos_item_code:
          posItemAtStorage.pos_item_code !== ''
            ? posItemAtStorage.pos_item_code
            : '',
        buy_one_get_one_free_pos_item_code:
          posItemAtStorage.buy_one_get_one_free_pos_item_code !== ''
            ? posItemAtStorage.buy_one_get_one_free_pos_item_code
            : '',
        posCode:
          posItemAtStorage.posCode !== ''
            ? posItemAtStorage.posCode
            : posItem.pos_item_code ?? '',
        promotionOneFreePosCode:
          posItemAtStorage.promotionOneFreePosCode !== ''
            ? posItemAtStorage.promotionOneFreePosCode
            : posItem.buy_one_get_one_free_pos_item_code ?? '',
      };
    }
  }

  private static addHandlingStateAtRefetch(posItem: PosItem) {
    return PosDataFactory.initialPosItem(posItem);
  }

  private static sortDescAtPosItemCode = (a: PosItem, b: PosItem) => {
    if (a.pos_item_code !== '' || b.pos_item_code !== '') {
      if (a.pos_item_code === '') return -1;
      if (b.pos_item_code === '') return 1;
    }
    return 0;
  };

  static createFromImportCSV(csvData: ImportPos[]): RegisterPos[] {
    const [header, ...body] = csvData;
    const headerData = header.data as Header[];
    const posCodeIndex = headerData.indexOf('POSコード');
    const PromotionOneFreePosCodeIndex =
      headerData.indexOf('もう一品無料商品のPOSコード');
    const metaDataIndex = headerData.indexOf('meta_data');

    //HACK: windowsでCSV生成した時に追加される配列の最後は不要なので削除
    if (body.slice(-1)[0].data.length === 1) {
      body.pop();
    }

    return body.map((item) => {
      return {
        pos_item_code: item.data[posCodeIndex],

        buy_one_get_one_free_pos_item_code:
          item.data[PromotionOneFreePosCodeIndex],

        meta_data: JSON.parse(
          this.removeLeftAndRightQuotation(item.data[metaDataIndex])
        ),
      };
    });
  }

  /** CSVデータで返ってくる "JSON" のダブルクォーテーションを削除する */
  private static removeLeftAndRightQuotation(metaData: string) {
    return metaData.slice(1).slice(0, -1);
  }

  private static isPromotionOneFree(options: PosOption[]) {
    return options.some(
      (o) => o.type === 'promotion' && o.value === 'buy_one_get_one_free'
    );
  }

  static createCSVFromItems(
    posItemData: PosItemAndHandlePos[],
    options: PosOption[]
  ): CSVItem[] {
    const isPromotionOneFree = this.isPromotionOneFree(options);

    return posItemData.map((posItem) => {
      const promotionOneFree = posItem.buy_one_get_one_free_pos_item_code
        ? posItem.buy_one_get_one_free_pos_item_code
        : '';
      const auth_store_name = posItem.auth_store_name;
      const delivery_service_name = posItem.delivery_service_name;
      const brand_name = posItem.brand_name;

      // meta-dataに含まれる'"'(ダブルクォーテーション1つ)をすべて'""'(ダブルクォーテーション2つ)に変更することで
      // CSV出力時に','(カンマ)を文字列として認識させ、meta-data全てを1つのセルに出力させることができる
      const metadata = JSON.stringify(posItem.meta_data).replace(/"/g, '""');

      return {
        item_name: CSVDataDomainService.addDoubleQuotationText(
          posItem.item_name
        ),
        brand_name,
        delivery_service_name,
        auth_store_name,
        pos_item_code: posItem.pos_item_code ? posItem.pos_item_code : '',
        buy_one_get_one_free_pos_item_code: isPromotionOneFree
          ? promotionOneFree
          : undefined,
        meta_data: `""${metadata}""`,
      };
    });
  }

  static createCSVHeaderFromItems(
    options: PosOption[],
    posDataLevel: PosDataLevel
  ): MappingPosDataLevelCsvData[] {
    const isPromotionOneFree = this.isPromotionOneFree(options);

    if (isPromotionOneFree) {
      return mappingPosDataLevel[posDataLevel].csvData;
    } else {
      return (
        /* TODO 下記の`as`はHACK/* TODO 下記の`as`はHACK的なやり方、typescriptのverv4.3.5であれば解消できる様子 */
        (
          mappingPosDataLevel[posDataLevel]
            .csvData as MappingPosDataLevelCsvData[]
        ).filter(
          (csvData) => csvData.key !== 'buy_one_get_one_free_pos_item_code'
        )
      );
    }
  }

  static createUploadPosDataFromItemTable(
    uploadPosData: PosItemAndHandlePos[]
  ): RegisterPos[] {
    return uploadPosData.map((item) => {
      return {
        pos_item_code: item.posCode,

        buy_one_get_one_free_pos_item_code: item.promotionOneFreePosCode,

        meta_data: item.meta_data,
      };
    });
  }

  static createFileName(): string {
    const date = new Date();
    const { yyyymmdd } = DateDomainService;
    return `file_` + yyyymmdd(date) + `.csv`;
  }
}
